import { useCallback, useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next"; // Import useTranslation

import { OpenAPI, TastersService } from "api/client";

import { StepFormFieldProps, Taster } from "interfaces/tasters";

import { validateEmail } from "utils/validators";

import { CONSTANTS } from "constants/constants";

import {
  educationSelectOptions,
  genderSelectOptions,
  initialTasterData,
  smokingSelectOptions,
} from "../constants";

const useMainLayoutContainer = () => {
  const { t } = useTranslation(); // Initialize translation

  const [mobileMenuIsOpen, setMobileMenuIsOpen] = useState<boolean>(false);
  const [activeItem, setActiveItem] = useState<string>(t("my_experiments"));
  const [tasterData, setTasterData] = useState<Taster>(initialTasterData);
  const [tasterFormData, setTasterFormData] =
    useState<Taster>(initialTasterData);
  const [loading, setLoading] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [validFieldErrors, setValidFieldErrors] = useState<string[]>([]);
  const [errorText, setErrorText] = useState<string>("");
  const [tasterMenuIsOpen, setTasterMenuIsOpen] = useState<boolean>(false);
  const [successToast, setSuccessToast] = useState<string | null>(null);
  const [errorToast, setErrorToast] = useState<string | null>(null);
  const [tasterDeleteModalIsOpen, setTasterDeleteModalIsOpen] =
    useState<boolean>(false);

  const [logoutModalIsOpen, setLogoutModalIsOpen] = useState<boolean>(false);

  const token = localStorage.getItem(CONSTANTS.access_token_key);

  const onLogOut = useCallback(() => {
    localStorage.removeItem(CONSTANTS.access_token_key);
    const baseUrl = (window as any).aka_env?.API_URL || OpenAPI.BASE;
    window.location.href = baseUrl + "/v1/logout";
  }, []);

  const getTasterData = useCallback(() => {
    setLoading(true);

    TastersService.readOneV1TastersCurrentGet()
      .then((response) => {
        setTasterData(response);
        setTasterFormData({
          ...response,
          dateOfBirth: response.dateOfBirth
            ? dayjs(response.dateOfBirth)
            : response.dateOfBirth,
        });
      })
      .catch((error) => {
        console.log(error, "ERROR");
        onLogOut();
      })
      .finally(() => setLoading(false));
  }, [onLogOut]);

  useEffect(() => {
    if (token) {
      getTasterData();
    }
  }, [getTasterData, token]);

  const handleMobileMenuOpen = useCallback(() => setMobileMenuIsOpen(true), []);

  const handleMobileMenuClose = useCallback(
    () => setMobileMenuIsOpen(false),
    []
  );

  const handleActiveItemChange = useCallback(
    (item: string) => setActiveItem(item),
    []
  );

  const handleStationChange = useCallback(
    (station: string) => {
      setTasterData((prev) => ({
        ...prev,
        station: station,
      }));
    }, []);

  const handleTasterMenuOpen = useCallback(() => setTasterMenuIsOpen(true), []);

  const handleTasterMenuClose = useCallback(() => {
    setTasterMenuIsOpen(false);
    setTasterFormData({
      ...tasterData,
      dateOfBirth: tasterData.dateOfBirth
        ? dayjs(tasterData.dateOfBirth)
        : null,
    });
    setValidFieldErrors([]);
  }, [tasterData]);

  const handleToastClose = useCallback(
    (type: "error" | "success") => () => {
      if (type === "success") {
        setSuccessToast(null);
      } else {
        setErrorToast(null);
      }
    },
    []
  );

  const handleFieldChange = useCallback(
    (name: keyof typeof initialTasterData) => (value: string | string[]) => {
      if (
        typeof value === "string" &&
        value[value.length - 1] === " " &&
        (name === "firstName" || name === "lastName")
      )
        return;

      setTasterFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
      setValidFieldErrors((prev) =>
        prev.filter((error: string) => error !== name)
      );
      setErrorText("");
    },
    []
  );

  const checkFieldValidation = useCallback(
    (value: string, key: string) => () => {
      if (!value) {
        setValidFieldErrors((prevErrors) =>
          prevErrors.includes(key) ? prevErrors : [...prevErrors, key]
        );
      } else if (key === "email" && !validateEmail(value)) {
        setValidFieldErrors((prevErrors) =>
          prevErrors.includes(key) ? prevErrors : [...prevErrors, key]
        );

        setErrorText(t("valid_email_error"));
      }
    },
    [t]
  );

  const checkFieldsValidation = useCallback(
    (values: { [key: string]: string | null | undefined }) =>
      Object.entries(values).forEach(([key, value]) => {
        if (!value) {
          setValidFieldErrors((prevErrors) =>
            prevErrors.includes(key) ? prevErrors : [...prevErrors, key]
          );
        } else if (key === "email" && !validateEmail(value)) {
          setValidFieldErrors((prevErrors) =>
            prevErrors.includes(key) ? prevErrors : [...prevErrors, key]
          );

          setErrorText(t("valid_email_error"));
        }
      }),
    [t]
  );

  const requiredFieldsData = useMemo(() => {
    const values = {
      firstName: tasterFormData.firstName,
      lastName: tasterFormData.lastName,
      email: tasterFormData.email,
      phoneNumber: tasterFormData.phoneNumber,
    };

    return {
      values,
      isValid:
        Object.values(values).every((el) => el) &&
        validateEmail(tasterFormData.email),
    };
  }, [tasterFormData]);

  const fields: StepFormFieldProps[] = useMemo(
    () => [
      {
        type: "input",
        placeholder: t("enter_taster_first_name"),
        label: t("enter_taster_first_name"),
        required: true,
        value: tasterFormData.firstName,
        onChange: handleFieldChange("firstName"),
        disabled: submitting,
        id: "taster-first-name",
        error: validFieldErrors.includes("firstName"),
        errorText: t("enter_first_name_error"),
        onBlur: checkFieldValidation(
          tasterFormData.firstName as string,
          "firstName"
        ),
      },
      {
        type: "input",
        placeholder: t("enter_taster_last_name"),
        label: t("enter_taster_last_name"),
        required: true,
        value: tasterFormData.lastName,
        onChange: handleFieldChange("lastName"),
        disabled: submitting,
        id: "taster-last-name",
        error: validFieldErrors.includes("lastName"),
        errorText: t("enter_last_name_error"),
        onBlur: checkFieldValidation(
          tasterFormData.lastName as string,
          "lastName"
        ),
      },
      {
        type: "input",
        placeholder: t("enter_taster_email"),
        label: t("enter_taster_email"),
        required: true,
        value: tasterFormData.email,
        onChange: handleFieldChange("email"),
        disabled: submitting,
        id: "taster-email",
        error: validFieldErrors.includes("email"),
        errorText: errorText || t("enter_email_error"),
        onBlur: checkFieldValidation(tasterFormData.email, "email"),
      },
      {
        type: "phone",
        placeholder: t("phone_number"),
        label: t("phone_number"),
        required: true,
        value: tasterFormData.phoneNumber,
        onChange: handleFieldChange("phoneNumber"),
        disabled: submitting,
        error: validFieldErrors.includes("phoneNumber"),
        errorText: t("enter_phone_error"),
        onBlur: checkFieldValidation(
          tasterFormData.phoneNumber as string,
          "phoneNumber"
        ),
      },
      {
        type: "date",
        label: t("date_of_birth"),
        value: tasterFormData.dateOfBirth,
        onChange: handleFieldChange("dateOfBirth"),
        disabled: submitting,
      },
      {
        type: "select",
        placeholder: t("gender"),
        label: t("gender"),
        value: tasterFormData.gender,
        options: genderSelectOptions,
        onChange: handleFieldChange("gender"),
        disabled: submitting,
      },
      {
        type: "select",
        placeholder: t("education_status"),
        label: t("education_status"),
        value: tasterFormData.education,
        options: educationSelectOptions,
        onChange: handleFieldChange("education"),
        disabled: submitting,
      },
      {
        type: "select",
        placeholder: t("smoking_status"),
        label: t("smoking_status"),
        value: tasterFormData.smoking,
        options: smokingSelectOptions,
        onChange: handleFieldChange("smoking"),
        disabled: submitting,
      },
    ],

    [
      t, // Add translation function to dependencies
      tasterFormData.firstName,
      tasterFormData.lastName,
      tasterFormData.email,
      tasterFormData.phoneNumber,
      tasterFormData.dateOfBirth,
      tasterFormData.gender,
      tasterFormData.education,
      tasterFormData.smoking,
      handleFieldChange,
      submitting,
      validFieldErrors,
      checkFieldValidation,
      errorText,
    ]
  );

  const handleTasterUpdate = useCallback(() => {
    if (requiredFieldsData.isValid) {
      const updatedTaster = {
        ...tasterFormData,
        dateOfBirth: tasterFormData.dateOfBirth
          ? dayjs(tasterFormData.dateOfBirth)?.format()
          : null,
        gender: tasterFormData.gender,
        education: tasterFormData.education,
        smoking: tasterFormData.smoking,
      };

      if (token && tasterData.id) {
        setSubmitting(true);

        TastersService.updateV1TastersIdPut(tasterData.id, updatedTaster)
          .then(() => {
            setSuccessToast(t("taster_update_success"));
            getTasterData();
          })
          .catch(() => {
            setErrorToast(t("taster_update_failure"));
          })
          .finally(() => {
            setTasterMenuIsOpen(false);
            setSubmitting(false);
          });
      }
    } else {
      checkFieldsValidation(requiredFieldsData.values);
    }
  }, [
    t, // Add translation function to dependencies
    checkFieldsValidation,
    getTasterData,
    requiredFieldsData.isValid,
    requiredFieldsData.values,
    tasterData.id,
    tasterFormData,
    token,
  ]);

  const handleTasterAvailabilityUpdate = useCallback(
    (availability: boolean) => {
      if (requiredFieldsData.isValid) {
        const updatedTaster = {
          availableForExperiments: availability,
        };

        if (token && tasterData.id) {
          setSubmitting(true);

          TastersService.updateV1TastersIdPut(tasterData.id, updatedTaster)
            .then(() => {
              setSuccessToast(
                availability === true
                  ? t("taster_availability_on")
                  : t("taster_availability_off")
              );
              getTasterData();
            })
            .catch(() => {
              setErrorToast(t("taster_update_failure"));
            })
            .finally(() => {
              setTasterMenuIsOpen(false);
              setSubmitting(false);
            });
        }
      }
    },
    [t, getTasterData, requiredFieldsData.isValid, tasterData.id, token]
  );

  const handDeleteTasterModalOpen = useCallback(
    () => setTasterDeleteModalIsOpen(true),
    []
  );
  const handDeleteTasterModalClose = useCallback(
    () => setTasterDeleteModalIsOpen(false),
    []
  );

  const handleLogOutModalOpen = useCallback(
    () => setLogoutModalIsOpen(true),
    []
  );
  const handleLogOutModalClose = useCallback(
    () => setLogoutModalIsOpen(false),
    []
  );

  const handleTasterDelete = useCallback(() => {
    TastersService.deleteV1TastersIdDelete(tasterData.id as string)
      .then(() => onLogOut())
      .catch(() => {
        setErrorToast(t("error_toast_message"));
      });
  }, [onLogOut, t, tasterData.id]);

  return {
    mobileMenuIsOpen,
    activeItem,
    tasterData,
    loading,
    submitting,
    fields,
    validFieldErrors,
    errorText,
    tasterMenuIsOpen,
    successToast,
    errorToast,
    logoutModalIsOpen,
    tasterDeleteModalIsOpen,
    handleMobileMenuOpen,
    handleMobileMenuClose,
    handleActiveItemChange,
    handleTasterMenuOpen,
    handleTasterMenuClose,
    handleTasterUpdate,
    handleToastClose,
    handleTasterAvailabilityUpdate,
    handleTasterDelete,
    handleLogOutModalOpen,
    handleLogOutModalClose,
    onLogOut,
    handDeleteTasterModalOpen,
    handDeleteTasterModalClose,
    handleStationChange,
  };
};

export default useMainLayoutContainer;
